import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useOwnerService } from '../contexts/OwnerServiceContext';
import { useApplicationReview } from '../contexts/ApplicationReviewContext';
import { brtaBadge, inMyQueue, queueMeta, queueOf, queueTabs, typeLabel } from '../utils/applicationQueue';
import { ownerInfo } from '../utils/applicationRecords';
import { fromBn, toBn } from '../utils/bn';
import type { OwnerApplication } from '../types/owner';
import type { QueueKey } from '../types/review';

export interface AppFilter {
  appNo: string;
  reg: string;
  owner: string;
  mobile: string;
  nid: string;
  type: string;
  date: string;
  status: string;
  division: string;
  officer: string;
}

export const ALL = 'সকল';
export const emptyFilter: AppFilter = { appNo: '', reg: '', owner: '', mobile: '', nid: '', type: ALL, date: '', status: ALL, division: ALL, officer: ALL };

const norm = (s: string) => fromBn(s).toLowerCase().replace(/\s+/g, '');
const has = (hay: string, needle: string) => !needle.trim() || norm(hay).includes(norm(needle));

export function useApplicationList() {
  const { applications, registrations } = useOwnerService();
  const { role, user, getReview } = useApplicationReview();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as QueueKey | null) ?? 'all';
  const [draft, setDraft] = useState<AppFilter>(emptyFilter);
  const [applied, setApplied] = useState<AppFilter>(emptyFilter);

  const officerOf = (a: OwnerApplication) => getReview(a).assignedOfficer;

  const inTab = (a: OwnerApplication, k: QueueKey) => {
    if (k === 'all') return true;
    if (k === 'mine') return inMyQueue(a, role, user.user, officerOf(a));
    return queueOf(a) === k;
  };

  const counts = useMemo(
    () => Object.fromEntries(queueTabs.map((t) => [t.key, applications.filter((a) => inTab(a, t.key)).length])) as Record<QueueKey, number>,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [applications, role, getReview]
  );

  const rows = applications.filter((a) => {
    if (!inTab(a, tab)) return false;
    const f = applied;
    const info = ownerInfo(a, registrations);
    return (
      has(a.id, f.appNo) &&
      has(a.vehicleReg, f.reg) &&
      has(a.ownerName, f.owner) &&
      has(a.ownerMobile, f.mobile) &&
      has(info.nid, f.nid) &&
      (f.type === ALL || typeLabel(a) === f.type) &&
      has(a.submitted, f.date) &&
      (f.status === ALL || queueMeta[queueOf(a)].label === f.status) &&
      (f.division === ALL || a.division === f.division) &&
      (f.officer === ALL || officerOf(a) === f.officer)
    );
  });

  const setTab = (k: QueueKey) => setParams(k === 'all' ? {} : { tab: k });

  const search = () => {
    setApplied(draft);
    toast.success(`${toBn(rowsFor(draft).length)}টি আবেদন পাওয়া গেছে`);
  };

  const rowsFor = (f: AppFilter) => applications.filter((a) => inTab(a, tab) && has(a.id, f.appNo) && has(a.vehicleReg, f.reg) && has(a.ownerName, f.owner) && has(a.ownerMobile, f.mobile));

  const reset = () => {
    setDraft(emptyFilter);
    setApplied(emptyFilter);
  };

  const exportExcel = () => {
    const header = ['ক্রমিক', 'আবেদন নম্বর', 'আবেদনের তারিখ', 'মালিকের নাম', 'মোবাইল', 'গাড়ির নম্বর', 'গাড়ির ধরন', 'BRTA যাচাই', 'আবেদন অবস্থা', 'ট্রাফিক বিভাগ', 'দায়িত্বপ্রাপ্ত কর্মকর্তা'];
    const lines = rows.map((a, i) => [i + 1, a.id, a.submitted, a.ownerName, a.ownerMobile, a.vehicleReg, typeLabel(a), brtaBadge(a).label, queueMeta[queueOf(a)].label, a.division, officerOf(a) ?? '—']);
    const csv = '\uFEFF' + [header, ...lines].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'owner-vehicle-applications.csv';
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`${toBn(rows.length)}টি আবেদন Excel (CSV) ফাইলে এক্সপোর্ট হয়েছে`);
  };

  return { tab, setTab, counts, rows, draft, setDraft, search, reset, exportExcel, officerOf };
}
